import { useState } from 'react'
import {
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Alert,
  Box,
  Divider,
  Chip,
  Typography,
  alpha,
} from '@mui/material'
import {
  MoreVert,
  Edit,
  Build,
  PlayArrow,
  Pause,
  Delete,
  Visibility,
  Lock,
  Science,
} from '@mui/icons-material'
import { DatePicker, TimePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import Swal from 'sweetalert2'
import api from '../../api/axiosInstance'
import { useNavigate, useParams } from 'react-router-dom'
import { cookies } from '../../utils/cookie'
import { jwtDecode } from 'jwt-decode'
import { useSelector } from 'react-redux'
import { selectUser } from '../../redux/features/auth/authSelectors'
import { getUserPlan } from '../../utils/plan'

dayjs.extend(utc)
dayjs.extend(timezone)

const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone

export default function MenuButton({ monitor, monitors, setMonitors }) {
  const userReducer = useSelector(selectUser)
  const plan = getUserPlan(userReducer)
  const [anchorEl, setAnchorEl] = useState(null)
  const [openDialog, setOpenDialog] = useState(false)
  const [startDate, setStartDate] = useState(dayjs())
  const [endDate, setEndDate] = useState(dayjs())
  const [startTime, setStartTime] = useState(dayjs())
  const [endTime, setEndTime] = useState(dayjs())
  const navigate = useNavigate()
  const params = useParams()

  // Admin monitör listesinde ayrı bir "göz" (detay) ikonu zaten var; bu yüzden
  // menüdeki Detay öğesini yalnızca admin olmayanlar için gösteriyoruz.
  const role = (() => {
    try {
      return jwtDecode(cookies.get('jwt-access')).role
    } catch {
      return undefined
    }
  })()

  const open = Boolean(anchorEl)

  const handleClick = (event) => setAnchorEl(event.currentTarget)
  const handleClose = () => setAnchorEl(null)

  const handleEdit = () => {
    const token = cookies.get('jwt-access')
    const role = jwtDecode(token).role
    if (role === 'admin') {
      navigate(`/admin/userMonitors/${params.userId}/${monitor.id}/monitor`)
    } else {
      navigate(`/user/monitors/${monitor.id}/monitor`)
    }
    handleClose()
  }

  const handleDetail = () => {
    const token = cookies.get('jwt-access')
    const role = jwtDecode(token).role
    if (role === 'admin') {
      navigate(`/admin/userMonitors/${params.userId}/${monitor.id}/detail`)
    } else {
      navigate(`/user/monitors/${monitor.id}/detail`)
    }
    handleClose()
  }

  const handleTest = async () => {
    Swal.fire({
      title: 'İzleme Test Ediliyor',
      html: 'Lütfen bekleyiniz...',
      allowOutsideClick: false,
      showConfirmButton: false,
      background: alpha('#0f172a', 0.98),
      color: '#fff',
      didOpen: async () => {
        Swal.showLoading()

        try {
          const response = await api.get(`monitors/instant-Control/${monitor.id}`)
          const result = response.data

          Swal.fire({
            title: 'Test Sonuçları',
            html: `
              <div style="background: ${alpha('#0f172a', 0.95)}; padding: 20px; border-radius: 12px; border: 1px solid ${alpha('#10b981', 0.3)};">
                <table style="width: 100%; color: #fff; font-size: 0.9rem;">
                  <tr>
                    <td style="padding: 8px; font-weight: 600;">Durum:</td>
                    <td style="padding: 8px; color: ${result.isError ? '#ef4444' : '#10b981'};">
                      ${String(result.status).toUpperCase()}
                    </td>
                  </tr>
                  <tr>
                    <td style="padding: 8px; font-weight: 600;">Yanıt Süresi:</td>
                    <td style="padding: 8px; color: #10b981;">${result.responseTime} ms</td>
                  </tr>
                  <tr>
                    <td style="padding: 8px; font-weight: 600;">Sonuç:</td>
                    <td style="padding: 8px; color: ${result.isError ? '#ef4444' : '#10b981'};">
                      ${result.isError ? 'BAŞARISIZ' : 'BAŞARILI'}
                    </td>
                  </tr>
                  <tr>
                    <td style="padding: 8px; font-weight: 600;">Zaman:</td>
                    <td style="padding: 8px; color: rgba(255,255,255,0.7);">
                      ${new Date().toLocaleString('tr-TR')}
                    </td>
                  </tr>
                </table>
              </div>
            `,
            icon: result.isError ? 'error' : 'success',
            confirmButtonText: 'Tamam',
            background: alpha('#0f172a', 0.98),
            color: '#fff',
            confirmButtonColor: '#10b981',
          })
        } catch (error) {
          Swal.fire({
            title: 'Hata!',
            text: 'Test tamamlanamadı.',
            icon: 'error',
            confirmButtonText: 'Tamam',
            background: alpha('#0f172a', 0.98),
            color: '#fff',
            confirmButtonColor: '#10b981',
          })
        }
      },
    })
    handleClose()
  }

  const handleToggle = async () => {
    try {
      if (monitor.isActiveByOwner) {
        await api.patch(`monitors/${monitor.id}/pause`)
        Swal.fire({
          icon: 'success',
          title: 'İzleme Durduruldu',
          text: 'İzleme başarıyla durduruldu.',
          confirmButtonText: 'Tamam',
          background: alpha('#0f172a', 0.98),
          color: '#fff',
          confirmButtonColor: '#10b981',
        })
        setMonitors((prev) =>
          prev.map((m) =>
            m.id === monitor.id ? { ...m, isActiveByOwner: false, status: 'uncertain' } : m
          )
        )
      } else {
        await api.patch(`monitors/${monitor.id}/play`)
        Swal.fire({
          icon: 'success',
          title: 'İzleme Başlatıldı',
          text: 'İzleme başarıyla başlatıldı.',
          confirmButtonText: 'Tamam',
          background: alpha('#0f172a', 0.98),
          color: '#fff',
          confirmButtonColor: '#10b981',
        })
        setMonitors((prev) =>
          prev.map((m) =>
            m.id === monitor.id ? { ...m, isActiveByOwner: true, status: 'uncertain' } : m
          )
        )
      }
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Hata',
        text: 'İşlem gerçekleştirilemedi.',
        confirmButtonText: 'Tamam',
        background: alpha('#0f172a', 0.98),
        color: '#fff',
        confirmButtonColor: '#10b981',
      })
    }
    handleClose()
  }

  const handleDelete = () => {
    Swal.fire({
      title: 'Silmek istediğinizden emin misiniz?',
      icon: 'warning',
      text: 'İzleme sistemden tamamen silinecektir',
      showCancelButton: true,
      confirmButtonText: 'Evet, Sil',
      cancelButtonText: 'İptal',
      confirmButtonColor: '#ef4444',
      cancelButtonColor: '#10b981',
      background: alpha('#0f172a', 0.98),
      color: '#fff',
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await api.delete(`monitors/${monitor.id}`)
          Swal.fire({
            icon: 'success',
            title: 'Silindi',
            text: 'İzleme başarıyla silindi.',
            confirmButtonText: 'Tamam',
            background: alpha('#0f172a', 0.98),
            color: '#fff',
            confirmButtonColor: '#10b981',
          })
          setMonitors((prev) => prev.filter((m) => m.id !== monitor.id))
        } catch (error) {
          Swal.fire({
            icon: 'error',
            title: 'Hata',
            text: error.response?.data?.message || 'Silme işlemi başarısız.',
            confirmButtonText: 'Tamam',
            background: alpha('#0f172a', 0.98),
            color: '#fff',
            confirmButtonColor: '#10b981',
          })
        }
      }
    })
    handleClose()
  }

  const handleMaintenance = async () => {
    const startDateTime = startDate.tz(userTimezone, true).toDate()
    startDateTime.setHours(startTime.hour())
    startDateTime.setMinutes(startTime.minute())
    startDateTime.setSeconds(0)

    const endDateTime = endDate.tz(userTimezone, true).toDate()
    endDateTime.setHours(endTime.hour())
    endDateTime.setMinutes(endTime.minute())
    endDateTime.setSeconds(0)

    const now = dayjs().tz(userTimezone).toDate()

    if (endDateTime < now) {
      Swal.fire({
        title: 'Hata',
        text: 'Bitiş tarihi geçmişte olamaz',
        icon: 'warning',
        background: alpha('#0f172a', 0.98),
        color: '#fff',
        confirmButtonColor: '#10b981',
      })
      return
    }

    if (startDateTime >= endDateTime) {
      Swal.fire({
        title: 'Hata',
        text: 'Başlangıç tarihi bitiş tarihinden önce olmalı',
        icon: 'warning',
        background: alpha('#0f172a', 0.98),
        color: '#fff',
        confirmButtonColor: '#10b981',
      })
      return
    }

    try {
      await api.post(`monitors/maintenance/${monitor.id}`, {
        startTime: startDateTime,
        endTime: endDateTime,
      })
      setMonitors((prev) =>
        prev.map((m) =>
          m.id === monitor.id
            ? {
                ...m,
                maintenance: { startTime: startDateTime, endTime: endDateTime, status: true },
              }
            : m
        )
      )
      setOpenDialog(false)
      Swal.fire({
        title: 'Başarılı',
        text: 'Bakım modu başarıyla ayarlandı',
        icon: 'success',
        background: alpha('#0f172a', 0.98),
        color: '#fff',
        confirmButtonColor: '#10b981',
      })
    } catch (error) {
      Swal.fire({
        title: 'Hata',
        text: 'Bakım modu ayarlanamadı',
        icon: 'error',
        background: alpha('#0f172a', 0.98),
        color: '#fff',
        confirmButtonColor: '#10b981',
      })
    }
  }

  const handleCancelMaintenance = async () => {
    try {
      await api.patch(`monitors/maintenance/${monitor.id}`)
      setMonitors((prev) =>
        prev.map((m) =>
          m.id === monitor.id
            ? { ...m, maintenance: { ...m.maintenance, status: false }, status: 'uncertain' }
            : m
        )
      )
      setOpenDialog(false)
      Swal.fire({
        title: 'Başarılı',
        text: 'Bakım modu iptal edildi',
        icon: 'success',
        background: alpha('#0f172a', 0.98),
        color: '#fff',
        confirmButtonColor: '#10b981',
      })
    } catch (error) {
      Swal.fire({
        title: 'Hata',
        text: 'Bakım modu iptal edilemedi',
        icon: 'error',
        background: alpha('#0f172a', 0.98),
        color: '#fff',
        confirmButtonColor: '#10b981',
      })
    }
  }

  return (
    <>
      <IconButton
        onClick={handleClick}
        sx={{
          color: alpha('#fff', 0.7),
          transition: 'all 0.2s',
          '&:hover': {
            color: '#10b981',
            bgcolor: alpha('#10b981', 0.1),
          },
        }}
      >
        <MoreVert fontSize="small" />
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        PaperProps={{
          sx: {
            bgcolor: alpha('#0f172a', 0.98),
            backdropFilter: 'blur(20px)',
            border: `1px solid ${alpha('#10b981', 0.2)}`,
            borderRadius: 2,
            minWidth: 180,
            mt: 0.5,
          },
        }}
      >
        <MenuItem
          onClick={handleEdit}
          sx={{
            fontSize: '0.875rem',
            color: '#fff',
            py: 1.25,
            '&:hover': { bgcolor: alpha('#10b981', 0.15) },
          }}
        >
          <ListItemIcon>
            <Edit fontSize="small" sx={{ color: '#10b981' }} />
          </ListItemIcon>
          <ListItemText>Edit</ListItemText>
        </MenuItem>

        <MenuItem
          onClick={() => {
            setOpenDialog(true)
            handleClose()
          }}
          disabled={!plan?.features?.maintenance_mode}
          sx={{
            fontSize: '0.875rem',
            color: '#fff',
            py: 1.25,
            '&:hover': { bgcolor: alpha('#10b981', 0.15) },
          }}
        >
          <ListItemIcon>
            <Build fontSize="small" sx={{ color: plan?.features?.maintenance_mode ? '#6366f1' : alpha('#fff', 0.3) }} />
          </ListItemIcon>
          <ListItemText>Maintenance</ListItemText>
          {!plan?.features?.maintenance_mode && (
            <Lock sx={{ fontSize: 16, color: alpha('#fff', 0.3), ml: 1 }} />
          )}
        </MenuItem>

        <MenuItem
          onClick={handleTest}
          sx={{
            fontSize: '0.875rem',
            color: '#fff',
            py: 1.25,
            '&:hover': { bgcolor: alpha('#10b981', 0.15) },
          }}
        >
          <ListItemIcon>
            <Science fontSize="small" sx={{ color: '#10b981' }} />
          </ListItemIcon>
          <ListItemText>Test</ListItemText>
        </MenuItem>

        {role !== 'admin' && (
          <MenuItem
            onClick={handleDetail}
            sx={{
              fontSize: '0.875rem',
              color: '#fff',
              py: 1.25,
              '&:hover': { bgcolor: alpha('#10b981', 0.15) },
            }}
          >
            <ListItemIcon>
              <Visibility fontSize="small" sx={{ color: '#10b981' }} />
            </ListItemIcon>
            <ListItemText>Detail</ListItemText>
          </MenuItem>
        )}

        <MenuItem
          onClick={handleToggle}
          sx={{
            fontSize: '0.875rem',
            color: '#fff',
            py: 1.25,
            '&:hover': { bgcolor: alpha('#10b981', 0.15) },
          }}
        >
          <ListItemIcon>
            {monitor.isActiveByOwner ? (
              <Pause fontSize="small" sx={{ color: '#f59e0b' }} />
            ) : (
              <PlayArrow fontSize="small" sx={{ color: '#10b981' }} />
            )}
          </ListItemIcon>
          <ListItemText>{monitor.isActiveByOwner ? 'Pause' : 'Play'}</ListItemText>
        </MenuItem>

        <Divider sx={{ my: 0.5, borderColor: alpha('#10b981', 0.15) }} />

        <MenuItem
          onClick={handleDelete}
          sx={{
            fontSize: '0.875rem',
            color: '#ef4444',
            py: 1.25,
            '&:hover': { bgcolor: alpha('#ef4444', 0.15) },
          }}
        >
          <ListItemIcon>
            <Delete fontSize="small" sx={{ color: '#ef4444' }} />
          </ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem>
      </Menu>

      {/* Maintenance Dialog */}
      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        PaperProps={{
          sx: {
            bgcolor: alpha('#0f172a', 0.98),
            backdropFilter: 'blur(20px)',
            border: `1px solid ${alpha('#10b981', 0.2)}`,
            borderRadius: 2,
            minWidth: 500,
          },
        }}
      >
        <DialogTitle sx={{ color: '#fff', fontSize: '1.1rem', fontWeight: 600 }}>
          {monitor.maintenance?.status ? 'Cancel Maintenance Mode' : 'Set Maintenance Mode'}
        </DialogTitle>

        <DialogContent>
          <Alert
            severity={monitor.maintenance?.status ? 'warning' : 'info'}
            sx={{
              mb: 3,
              bgcolor: alpha(monitor.maintenance?.status ? '#f59e0b' : '#10b981', 0.1),
              color: '#fff',
              border: `1px solid ${alpha(monitor.maintenance?.status ? '#f59e0b' : '#10b981', 0.3)}`,
            }}
          >
            {monitor.maintenance?.status
              ? 'Maintenance mode is active. You can cancel and reinstall it.'
              : 'Tracking will be disabled and no notifications will be sent for the period you specify.'}
          </Alert>

          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <DatePicker
                  label="Start Date"
                  format="DD/MM/YYYY"
                  value={monitor?.maintenance?.status ? dayjs(monitor.maintenance.startTime) : startDate}
                  onChange={setStartDate}
                  disabled={monitor.maintenance?.status}
                  slotProps={{
                    textField: {
                      size: 'small',
                      fullWidth: true,
                      sx: {
                        '& .MuiOutlinedInput-root': {
                          color: '#fff',
                          '& fieldset': { borderColor: alpha('#10b981', 0.3) },
                          '&:hover fieldset': { borderColor: alpha('#10b981', 0.5) },
                          '&.Mui-focused fieldset': { borderColor: '#10b981' },
                        },
                        '& .MuiInputLabel-root': { color: alpha('#fff', 0.7) },
                      },
                    },
                  }}
                />
                <TimePicker
                  label="Start Time"
                  ampm={false}
                  timeSteps={{ minutes: 1 }}
                  value={monitor?.maintenance?.status ? dayjs(monitor.maintenance.startTime) : startTime}
                  onChange={setStartTime}
                  disabled={monitor.maintenance?.status}
                  slotProps={{
                    textField: {
                      size: 'small',
                      fullWidth: true,
                      sx: {
                        '& .MuiOutlinedInput-root': {
                          color: '#fff',
                          '& fieldset': { borderColor: alpha('#10b981', 0.3) },
                          '&:hover fieldset': { borderColor: alpha('#10b981', 0.5) },
                          '&.Mui-focused fieldset': { borderColor: '#10b981' },
                        },
                        '& .MuiInputLabel-root': { color: alpha('#fff', 0.7) },
                      },
                    },
                  }}
                />
              </Box>

              <Box sx={{ display: 'flex', gap: 2 }}>
                <DatePicker
                  label="End Date"
                  format="DD/MM/YYYY"
                  value={monitor?.maintenance?.status ? dayjs(monitor.maintenance.endTime) : endDate}
                  onChange={setEndDate}
                  disabled={monitor.maintenance?.status}
                  slotProps={{
                    textField: {
                      size: 'small',
                      fullWidth: true,
                      sx: {
                        '& .MuiOutlinedInput-root': {
                          color: '#fff',
                          '& fieldset': { borderColor: alpha('#10b981', 0.3) },
                          '&:hover fieldset': { borderColor: alpha('#10b981', 0.5) },
                          '&.Mui-focused fieldset': { borderColor: '#10b981' },
                        },
                        '& .MuiInputLabel-root': { color: alpha('#fff', 0.7) },
                      },
                    },
                  }}
                />
                <TimePicker
                  label="End Time"
                  ampm={false}
                  timeSteps={{ minutes: 1 }}
                  value={monitor?.maintenance?.status ? dayjs(monitor.maintenance.endTime) : endTime}
                  onChange={setEndTime}
                  disabled={monitor.maintenance?.status}
                  slotProps={{
                    textField: {
                      size: 'small',
                      fullWidth: true,
                      sx: {
                        '& .MuiOutlinedInput-root': {
                          color: '#fff',
                          '& fieldset': { borderColor: alpha('#10b981', 0.3) },
                          '&:hover fieldset': { borderColor: alpha('#10b981', 0.5) },
                          '&.Mui-focused fieldset': { borderColor: '#10b981' },
                        },
                        '& .MuiInputLabel-root': { color: alpha('#fff', 0.7) },
                      },
                    },
                  }}
                />
              </Box>
            </Box>
          </LocalizationProvider>
        </DialogContent>

        <DialogActions sx={{ p: 2.5, gap: 1 }}>
          <Button
            onClick={() => setOpenDialog(false)}
            sx={{
              color: alpha('#fff', 0.7),
              '&:hover': { bgcolor: alpha('#fff', 0.05) },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={monitor.maintenance?.status ? handleCancelMaintenance : handleMaintenance}
            variant="contained"
            sx={{
              bgcolor: monitor.maintenance?.status ? '#ef4444' : '#10b981',
              '&:hover': {
                bgcolor: monitor.maintenance?.status ? '#dc2626' : '#059669',
              },
            }}
          >
            {monitor.maintenance?.status ? 'Cancel Maintenance Mode' : 'Start Maintenance Mode'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}